import Paystack from '@paystack/inline-js'
import classNames from 'classnames'
import { FormikProps } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import ModalComponent from '..'
import {
  checkoutProviderMap,
  MONNIFY_API_KEY,
  MONNIFY_CONTRACT_CODE,
  MONNIFY_IS_TEST_MODE,
  terminalProviderMap,
} from '../../../constants'
import { POSOrderDetails } from '../../../features/pos-module'
import { formatCurrency } from '../../../helpers'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { useModalControl } from '../../../hooks/useModalControl'
import { updateSelectedPOSTerminal } from '../../../redux-toolkits/auth/auth.reducer'
import { useInitiateOrderPaymentMutation } from '../../../redux-toolkits/orders/orders.slice'
import { SingleOrder } from '../../../redux-toolkits/orders/orders.type'
import {
  CheckoutProvider,
  MonnifyCheckoutConfig,
  MonnifyCheckoutResponse,
  PaymentMethods,
} from '../../../redux-toolkits/cart/cart.type'
import {
  SingleTerminal,
  TerminalProvider,
} from '../../../redux-toolkits/terminals/terminals.types'
import { PaystackSuccessData } from '../../../redux-toolkits/cart/cart.type'
import { Button } from '../../button'
import { ActionChip } from '../../cards/actionChip'
import { Status } from '../../cards/statusTag'
import Icon from '../../icon'
import { EditInput } from '../../inputs/editInput'
import { TabNav } from '../../nav/tabNav'
import { POSPaymentMethodPOSModal } from './POSPaymentMethodPOS'
import { SelectTerminal } from './selectPOSTerminalModal'

const payStackPopup = new Paystack()
const MONNIFY_SDK_SRC = 'https://sdk.monnify.com/plugin/monnify.js'
const MONNIFY_OUTCOME_DEBOUNCE_MS = 500

// Monnify's checkout SDK occasionally calls `Node.removeChild` on a node it
// has already detached during teardown (overpayment, underpayment, "Back to
// merchant site"). That throws a synchronous `NotFoundError` from inside
// their code that we cannot try/catch from outside. We make `removeChild`
// tolerant of the already-detached case so the exception never escapes.
//
// The guard only changes behaviour for the exact bad call (removing a node
// whose parent isn't `this`); every other code path is unaffected. React's
// reconciler verifies parent before removal so it never hits this branch.
const installDomNotFoundErrorGuard = () => {
  if (typeof Node === 'undefined' || typeof Node.prototype === 'undefined') {
    return
  }

  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T,
  ): T {
    try {
      return originalRemoveChild.call(this, child) as T
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === 'NotFoundError' &&
        child &&
        child.parentNode !== this
      ) {
        return child
      }

      throw error
    }
  } as typeof Node.prototype.removeChild
}

installDomNotFoundErrorGuard()

type MonnifyCheckoutCallbacks = {
  onLoadStart?: () => void
  onLoadComplete?: () => void
  onComplete?: (response: MonnifyCheckoutResponse) => void
  onClose?: (data: MonnifyCheckoutResponse) => void
}

const getMonnifyCheckoutAmounts = (response?: MonnifyCheckoutResponse) => {
  const payable =
    response?.payableAmount ??
    response?.amount ??
    response?.authorizedAmount ??
    0
  const paid = response?.amountPaid ?? 0

  return { paid, payable }
}

const isMonnifyCheckoutCancelled = (response?: MonnifyCheckoutResponse) => {
  return (
    response?.paymentStatus === 'USER_CANCELLED' ||
    response?.responseCode === 'USER_CANCELLED'
  )
}

const isMonnifyCheckoutPending = (response?: MonnifyCheckoutResponse) => {
  return response?.paymentStatus === 'PENDING'
}

const isMonnifyCheckoutOverpaid = (response?: MonnifyCheckoutResponse) => {
  if (response?.paymentStatus === 'OVERPAID') {
    return true
  }

  const { paid, payable } = getMonnifyCheckoutAmounts(response)

  return payable > 0 && paid > payable
}

const isMonnifyCheckoutPartiallyPaid = (response?: MonnifyCheckoutResponse) => {
  if (response?.paymentStatus === 'PARTIALLY_PAID') {
    return true
  }

  const { paid, payable } = getMonnifyCheckoutAmounts(response)

  return payable > 0 && paid > 0 && paid < payable
}

const isMonnifyCheckoutPaid = (response?: MonnifyCheckoutResponse) => {
  if (response?.paymentStatus !== 'PAID') {
    return false
  }

  if (
    isMonnifyCheckoutOverpaid(response) ||
    isMonnifyCheckoutPartiallyPaid(response)
  ) {
    return false
  }

  const { paid, payable } = getMonnifyCheckoutAmounts(response)

  if (payable > 0 && paid > 0) {
    return paid === payable
  }

  return true
}

const hasMonnifyCheckoutOutcome = (response?: MonnifyCheckoutResponse) => {
  const { paid } = getMonnifyCheckoutAmounts(response)

  return Boolean(response?.paymentStatus || response?.responseCode || paid > 0)
}

const getMonnifyCheckoutErrorMessage = (response?: MonnifyCheckoutResponse) => {
  if (isMonnifyCheckoutOverpaid(response)) {
    const { paid, payable } = getMonnifyCheckoutAmounts(response)

    if (payable > 0 && paid > 0) {
      return `Overpayment received: ${formatCurrency(
        paid,
      )} was paid for an order of ${formatCurrency(
        payable,
      )}. The payment will be reversed. Please try again with the exact amount.`
    }
  }

  if (isMonnifyCheckoutPartiallyPaid(response)) {
    const { paid, payable } = getMonnifyCheckoutAmounts(response)

    if (payable > 0 && paid > 0) {
      return `Partial payment received: ${formatCurrency(
        paid,
      )} was paid for an order of ${formatCurrency(
        payable,
      )}. The payment will be reversed. Please try again with the full amount.`
    }
  }

  if (response?.responseMessage) {
    return response.responseMessage
  }

  if (isMonnifyCheckoutCancelled(response)) {
    return 'Payment was cancelled.'
  }

  if (response?.paymentStatus === 'PENDING') {
    return 'Payment is still pending. Please wait for confirmation or try again.'
  }

  return 'Payment was not completed. Please try again.'
}

const isPaystackCheckoutPaid = (data: PaystackSuccessData) => {
  return data.status?.toLowerCase() === 'success'
}

declare global {
  interface Window {
    MonnifySDK?: {
      initialize: (
        config: MonnifyCheckoutConfig & MonnifyCheckoutCallbacks,
      ) => void
    }
  }
}

const loadMonnifySDK = () => {
  return new Promise<void>((resolve, reject) => {
    if (window.MonnifySDK) {
      resolve()
      return
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${MONNIFY_SDK_SRC}"]`,
    )

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener('error', () => reject(), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = MONNIFY_SDK_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject()
    document.body.appendChild(script)
  })
}

const paymentMethods: {
  title: string
  icon: string
  value: PaymentMethods
  disabled: boolean
}[] = [
  {
    title: 'Bank transfer',
    icon: 'bank',
    value: 'bank_transfer',
    disabled: false,
  },
  {
    title: 'POS',
    icon: 'cash-register',
    value: 'pos',
    disabled: false,
  },
]

const checkoutProviderTabs = Object.keys(checkoutProviderMap).map((k) => {
  return {
    name: checkoutProviderMap[k as CheckoutProvider].text,
    id: k,
    disabled: false,
    className: '',
  }
})

export const POSPaymentMethod: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: () => void
  formik: FormikProps<POSOrderDetails>
  order: SingleOrder
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  formik,
  order,
}) => {
  const [terminals, setTerminals] = useState<SingleTerminal[]>([])
  const [editingTerminal, setEditingTerminal] = useState<boolean>(false)
  const monnifyCheckoutHandledRef = useRef(false)
  const monnifyPendingOutcomeRef = useRef<MonnifyCheckoutResponse | undefined>(
    undefined,
  )
  const monnifyOutcomeTimerRef = useRef<number | undefined>(undefined)
  const paystackCheckoutHandledRef = useRef(false)

  useEffect(() => {
    return () => {
      if (monnifyOutcomeTimerRef.current) {
        window.clearTimeout(monnifyOutcomeTimerRef.current)
      }
    }
  }, [])

  const { modal, modalOpen, handleModalClose, handleModalOpen, setModalOpen } =
    useModalControl()

  const { POSFulfilmentCenter } = useAppSelector((state) => state.auth)

  const dispatch = useAppDispatch()

  const [initiatePayment, { isLoading: initiatingPayment, data: paymentData }] =
    useInitiateOrderPaymentMutation()

  const { values, setFieldValue, errors, isValid } = formik

  const handlePaymentSuccess = () => {
    handleModalClose() // Close POS payment confirmation modal
    onCloseCallback() // Close payment method selection modal
    onSuccessCallback()
  }

  const getCustomerName = () => {
    const formName = values.customerName?.trim()

    if (formName) {
      return formName
    }

    return [
      order.personalInformation?.firstName,
      order.personalInformation?.lastName,
    ]
      .filter(Boolean)
      .join(' ')
  }

  const buildMonnifyCheckoutConfig = (
    paymentReference: string,
  ): MonnifyCheckoutConfig | undefined => {
    if (!MONNIFY_API_KEY || !MONNIFY_CONTRACT_CODE) {
      toast.error('Monnify checkout is not configured')
      return undefined
    }

    const customerEmail =
      values.customerEmail || order.personalInformation?.email || ''
    const customerFullName = getCustomerName()

    if (!customerEmail || !customerFullName) {
      toast.error('Customer name and email are required for Monnify checkout')
      return undefined
    }

    return {
      amount: order.amount,
      currency: order.currency || 'NGN',
      reference: paymentReference,
      customerFullName,
      customerEmail,
      apiKey: String(MONNIFY_API_KEY),
      contractCode: String(MONNIFY_CONTRACT_CODE),
      paymentDescription: `Order #${order.harmonizedReference}`,
      metadata: {
        harmonizedReference: order.harmonizedReference,
        orderId: order._id,
      },
      paymentMethods: ['ACCOUNT_TRANSFER'],
      isTestMode: MONNIFY_IS_TEST_MODE,
    }
  }

  const scheduleCheckoutSuccess = (
    handledRef: React.MutableRefObject<boolean>,
  ) => {
    if (handledRef.current) {
      return
    }

    handledRef.current = true
    window.setTimeout(() => {
      handlePaymentSuccess()
    }, 0)
  }

  const shouldQueueMonnifyOutcome = (response?: MonnifyCheckoutResponse) => {
    if (
      isMonnifyCheckoutPending(response) ||
      isMonnifyCheckoutCancelled(response)
    ) {
      return false
    }

    return (
      isMonnifyCheckoutPaid(response) || hasMonnifyCheckoutOutcome(response)
    )
  }

  const showMonnifyFailureToast = (response?: MonnifyCheckoutResponse) => {
    monnifyCheckoutHandledRef.current = true
    toast.error(getMonnifyCheckoutErrorMessage(response))
  }

  const processMonnifyOutcome = () => {
    try {
      if (monnifyCheckoutHandledRef.current) {
        return
      }

      const response = monnifyPendingOutcomeRef.current
      monnifyPendingOutcomeRef.current = undefined

      if (isMonnifyCheckoutPending(response)) {
        return
      }

      if (isMonnifyCheckoutPaid(response)) {
        monnifyCheckoutHandledRef.current = true
        handlePaymentSuccess()
        return
      }

      if (isMonnifyCheckoutCancelled(response)) {
        monnifyCheckoutHandledRef.current = true
        return
      }

      if (!hasMonnifyCheckoutOutcome(response)) {
        return
      }

      showMonnifyFailureToast(response)
    } catch (error) {
      console.error('Monnify checkout follow-up failed', error)
    }
  }

  const queueMonnifyOutcome = (response?: MonnifyCheckoutResponse) => {
    if (monnifyCheckoutHandledRef.current) {
      return
    }

    monnifyPendingOutcomeRef.current = response

    if (monnifyOutcomeTimerRef.current) {
      window.clearTimeout(monnifyOutcomeTimerRef.current)
    }

    monnifyOutcomeTimerRef.current = window.setTimeout(() => {
      monnifyOutcomeTimerRef.current = undefined
      processMonnifyOutcome()
    }, MONNIFY_OUTCOME_DEBOUNCE_MS)
  }

  const handleMonnifyCheckoutComplete = (
    response?: MonnifyCheckoutResponse,
  ) => {
    if (monnifyCheckoutHandledRef.current) {
      return
    }

    if (isMonnifyCheckoutCancelled(response)) {
      monnifyCheckoutHandledRef.current = true
      return
    }

    if (shouldQueueMonnifyOutcome(response)) {
      queueMonnifyOutcome(response)
    }
  }

  const handleMonnifyCheckoutClose = (response?: MonnifyCheckoutResponse) => {
    if (monnifyCheckoutHandledRef.current) {
      return
    }

    if (isMonnifyCheckoutCancelled(response)) {
      monnifyCheckoutHandledRef.current = true
      return
    }

    if (shouldQueueMonnifyOutcome(response)) {
      queueMonnifyOutcome(response)
    }
  }

  const handlePaystackCheckout = (accessCode?: string) => {
    if (!accessCode) {
      toast.error('Paystack checkout is unavailable')
      return
    }

    paystackCheckoutHandledRef.current = false

    payStackPopup.resumeTransaction(accessCode, {
      onSuccess: (successData) => {
        if (paystackCheckoutHandledRef.current) {
          return
        }

        if (isPaystackCheckoutPaid(successData)) {
          scheduleCheckoutSuccess(paystackCheckoutHandledRef)
          return
        }

        paystackCheckoutHandledRef.current = true
        toast.error(
          successData.message || 'Payment was not completed. Please try again.',
        )
      },
      onCancel: () => {
        if (paystackCheckoutHandledRef.current) {
          return
        }

        paystackCheckoutHandledRef.current = true
      },
      onError: (error) => {
        if (paystackCheckoutHandledRef.current) {
          return
        }

        paystackCheckoutHandledRef.current = true
        toast.error(
          error.message ||
            'Unable to process Paystack payment. Please try again.',
        )
      },
    })
  }

  const handleMonnifyCheckout = async (paymentReference?: string) => {
    if (!paymentReference) {
      toast.error('Monnify payment reference is unavailable')
      return
    }

    const config = buildMonnifyCheckoutConfig(paymentReference)

    if (!config) {
      return
    }

    try {
      monnifyCheckoutHandledRef.current = false
      monnifyPendingOutcomeRef.current = undefined

      if (monnifyOutcomeTimerRef.current) {
        window.clearTimeout(monnifyOutcomeTimerRef.current)
        monnifyOutcomeTimerRef.current = undefined
      }

      await loadMonnifySDK()

      window.MonnifySDK?.initialize({
        ...config,
        onComplete: handleMonnifyCheckoutComplete,
        onClose: handleMonnifyCheckoutClose,
      })
    } catch (error) {
      toast.error('Unable to load Monnify checkout')
    }
  }

  return (
    <>
      <ModalComponent
        open={open}
        setOpen={setOpen}
        onCloseCallback={() => {
          onCloseCallback()
        }}
        title={'Select payment method'}
        childrenClassName="md:!max-w-[420px]"
      >
        <div className="flex flex-col items-center justify-center gap-4">
          {paymentMethods.map((method) => {
            return (
              <button
                key={method.value}
                disabled={method.disabled}
                type="button"
                className={classNames(
                  'p-4 flex items-center w-full border rounded-md  gap-2 disabled:opacity-50 disabled:cursor-not-allowed',
                  {
                    'border-divider-gray':
                      method.value !== values.paymentMethod,
                    'border-primary text-primary':
                      method.value === values.paymentMethod,
                  },
                )}
                onClick={() => {
                  setFieldValue('paymentMethod', method.value)
                }}
              >
                <Icon id={method.icon} width={24} height={24} />
                <span>{method.title}</span>
              </button>
            )
          })}

          {values.paymentMethod === 'bank_transfer' ? (
            <div className="w-full flex flex-col gap-2">
              <span className="text-sm font-medium text-sec-black">
                Checkout provider
              </span>
              <TabNav
                activeTab={values.provider}
                setActiveTab={(provider) => {
                  setFieldValue('provider', provider)
                }}
                tabs={checkoutProviderTabs}
                className={''}
                tabClassName={''}
                type={'switcher'}
                loading={false}
              />
            </div>
          ) : undefined}

          {values.paymentMethod === 'pos' ? (
            <EditInput
              title={`POS Terminal`}
              value={undefined}
              onEditClick={() => {
                setEditingTerminal(true)
              }}
              errors={errors.terminal}
              onCancel={() => {
                setEditingTerminal(false)
              }}
              onEditSave={() => {
                if (values.terminalProvider === 'PAYSTACK') {
                  // Reset selected terminal for paystack option
                  dispatch(updateSelectedPOSTerminal(undefined))
                  setTerminals([])
                } else {
                  dispatch(updateSelectedPOSTerminal(terminals[0]))
                }
                setEditingTerminal(false)
              }}
              editing={editingTerminal}
              className={'col-span-full'}
              buttonText="Save terminal choice"
              buttonClassName="!max-w-fit ml-auto"
            >
              {({ isEditing }) => {
                return isEditing ? (
                  <div className="mb-4 flex flex-col gap-4">
                    <TabNav
                      activeTab={values.terminalProvider}
                      setActiveTab={() => {}}
                      tabs={Object.keys(terminalProviderMap).map((k) => {
                        return {
                          name: terminalProviderMap[k as TerminalProvider].text,
                          id: k,
                          disabled: false,
                          className: '',
                          onClick: () => {
                            setFieldValue('terminalProvider', k)
                          },
                        }
                      })}
                      className={''}
                      tabClassName={''}
                      type={'switcher'}
                      loading={false}
                    />

                    {values.terminalProvider === 'PAYSTACK' ? undefined : (
                      <SelectTerminal
                        maxSelection={1}
                        open={isEditing}
                        terminals={terminals}
                        setTerminals={setTerminals}
                        fulfilmentCenter={POSFulfilmentCenter}
                        onlyActive
                        provider={values.terminalProvider}
                        showSearch={false}
                      />
                    )}
                  </div>
                ) : (
                  <ActionChip
                    name={
                      <div className="flex items-center w-full gap-2">
                        <Icon id="cash-register" width={16} height={16} />

                        {values.terminalProvider === 'PAYSTACK'
                          ? 'Paystack POS'
                          : values?.terminal?.tag}

                        <Status
                          text={
                            terminalProviderMap[values.terminalProvider].text
                          }
                          type={
                            terminalProviderMap[values.terminalProvider]
                              .pillVariant
                          }
                          className="!border border-blue ml-auto"
                        />
                      </div>
                    }
                    icon={undefined}
                    className="border"
                  />
                )
              }}
            </EditInput>
          ) : undefined}

          {editingTerminal ? undefined : (
            <Button
              label="Continue"
              type="button"
              className="mt-2 w-full"
              disabled={initiatingPayment || !isValid}
              loading={initiatingPayment}
              primary
              onClick={async () => {
                const resp = await initiatePayment({
                  reference: order.harmonizedReference,
                  payload: {
                    email: values.customerEmail ?? '',
                    paymentMethod: values.paymentMethod,
                    redirectURL: '',
                    terminalId:
                      values?.paymentMethod === 'pos'
                        ? values.terminal?.terminalId
                        : undefined,
                    provider:
                      values?.paymentMethod === 'bank_transfer'
                        ? values.provider
                        : values?.paymentMethod === 'pos'
                          ? values.terminalProvider
                          : undefined,
                  },
                })

                if (resp.data?.success) {
                  if (values.paymentMethod === 'bank_transfer') {
                    if (values.provider === 'MONNIFY') {
                      await handleMonnifyCheckout(resp.data.data.paymentRef)
                    } else {
                      handlePaystackCheckout(resp.data.data.accessCode)
                    }
                  } else if (values.paymentMethod === 'pos') {
                    handleModalOpen('POSPaymentConfirmationModal')
                  }
                }
              }}
            />
          )}
        </div>
      </ModalComponent>

      {paymentData?.data &&
      modalOpen &&
      modal === 'POSPaymentConfirmationModal' ? (
        <POSPaymentMethodPOSModal
          open={modalOpen && modal === 'POSPaymentConfirmationModal'}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            handleModalClose()
          }}
          onSuccessCallback={() => {
            handlePaymentSuccess()
          }}
          reference={paymentData.data.paymentRef}
          qrSrc={paymentData.data.qrCode}
          provider={paymentData.data.provider}
        />
      ) : undefined}
    </>
  )
}
